// =============================================================================
//  src/minigames/ExerciseWidget.jsx
//  LOS SEIS TIPOS DE EJERCICIO, TAL Y COMO LOS JUEGA EL ALUMNO
//
//  Props:
//    exercise   : el objeto del CRM (type, question/statement, choices, pairs…)
//    onResuelto : (acierto: boolean) => void — se llama UNA vez, cuando el
//                 ejercicio queda cerrado. El que decide qué pasa después es
//                 DocPlayer, no esto.
//
//  ── POR QUÉ CADA TIPO ES SU PROPIO COMPONENTE (06-10-2026) ──────────────────
//  Antes todo esto era una sola función con un `if` por tipo, y los `useState`
//  de «completar», «vocabulario», «parejas» y «arrastrar» vivían DENTRO de esos
//  `if`. React exige que los hooks se llamen siempre en el mismo orden: un
//  componente que llama dos hooks cuando es un quiz y cuatro cuando es un
//  «completar» sólo funciona mientras nadie cambie el tipo sin remontarlo.
//
//  Aguantaba porque DocPlayer le pone una `key` distinta a cada pregunta, o
//  sea por una costumbre de otro fichero. Separarlo en componentes hace que la
//  regla se cumpla sola.
// =============================================================================
import React, { useState } from 'react';

export default function ExerciseWidget({ exercise, onResuelto }) {
    if (!exercise) return null;
    switch (exercise.type) {
        case 'truefalse':  return <VerdaderoFalso ex={exercise} onResuelto={onResuelto} />;
        case 'quiz':       return <Quiz           ex={exercise} onResuelto={onResuelto} />;
        case 'fill':       return <Completar      ex={exercise} onResuelto={onResuelto} />;
        case 'wordselect': return <Vocabulario    ex={exercise} onResuelto={onResuelto} />;
        case 'match':      return <Parejas        ex={exercise} onResuelto={onResuelto} />;
        case 'dragdrop':   return <Arrastrar      ex={exercise} onResuelto={onResuelto} />;
        default:           return null;
    }
}

/* ── VERDADERO / FALSO ───────────────────────────────────────────────────── */
function VerdaderoFalso({ ex, onResuelto }) {
    const [elegido, setElegido] = useState(null);
    const respondido = elegido !== null;

    const responde = (val) => {
        if (respondido) return;
        setElegido(val);
        onResuelto?.(val === ex.answer);
    };

    return (
        <div className="ew-root">
            <p className="ew-question">{ex.statement}</p>
            <div className="ew-tf-row">
                {[true, false].map(val => {
                    let cls = 'ew-tf-btn';
                    if (respondido) {
                        if (val === ex.answer) cls += ' correct';
                        else if (elegido === val) cls += ' wrong';
                        else cls += ' dim';
                    }
                    return (
                        <button key={String(val)} className={cls} onClick={() => responde(val)}>
                            {val ? '✅ Verdadero' : '❌ Falso'}
                        </button>
                    );
                })}
            </div>
            {respondido && <Explicacion texto={ex.explanation} />}
        </div>
    );
}

/* ── QUIZ ────────────────────────────────────────────────────────────────── */
function Quiz({ ex, onResuelto }) {
    const [elegido, setElegido] = useState(null);
    const respondido = elegido !== null;

    const responde = (i) => {
        if (respondido) return;
        setElegido(i);
        onResuelto?.(i === ex.correct);
    };

    return (
        <div className="ew-root">
            <p className="ew-question">{ex.question}</p>
            <div className="ew-choices">
                {(ex.choices || []).map((choice, i) => {
                    let cls = 'ew-choice';
                    if (respondido) {
                        if (i === ex.correct) cls += ' correct';
                        else if (i === elegido) cls += ' wrong';
                        else cls += ' dim';
                    }
                    return (
                        <button key={i} className={cls} onClick={() => responde(i)}>
                            <span className="ew-letter">{['A', 'B', 'C', 'D'][i] ?? i + 1}</span>
                            {choice}
                        </button>
                    );
                })}
            </div>
            {respondido && <Explicacion texto={ex.explanation} />}
        </div>
    );
}

/* ── COMPLETAR ───────────────────────────────────────────────────────────── */
function Completar({ ex, onResuelto }) {
    const [valores, setValores] = useState((ex.blanks || []).map(() => ''));
    const [enviado, setEnviado] = useState(false);
    const trozos = (ex.template || '').split('____');

    const comprueba = () => {
        setEnviado(true);
        onResuelto?.((ex.blanks || []).every((b, i) => valores[i] === b));
    };

    return (
        <div className="ew-root">
            <div className="ew-fill-template">
                {trozos.map((trozo, i) => (
                    <React.Fragment key={i}>
                        <span>{trozo}</span>
                        {i < trozos.length - 1 && ex.choices?.[i] && (
                            <select
                                className={`ew-fill-select ${enviado ? (valores[i] === ex.blanks[i] ? 'correct' : 'wrong') : ''}`}
                                value={valores[i]}
                                disabled={enviado}
                                onChange={e => {
                                    const n = [...valores]; n[i] = e.target.value; setValores(n);
                                }}
                            >
                                <option value="">— elegir —</option>
                                {ex.choices[i].map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        )}
                    </React.Fragment>
                ))}
            </div>
            {!enviado && <button className="ew-check-btn" onClick={comprueba}>Check</button>}
            {enviado && (
                <>
                    <p className="ew-respuestas">Answers: {(ex.blanks || []).join(', ')}</p>
                    <Explicacion texto={ex.explanation} />
                </>
            )}
        </div>
    );
}

/* ── VOCABULARIO ─────────────────────────────────────────────────────────── */
function Vocabulario({ ex, onResuelto }) {
    const palabras = (ex.text || '').split(' ');
    const [elegidas, setElegidas] = useState([]);
    const [enviado, setEnviado] = useState(false);

    const limpia = (w) => w.replace(/[^a-zA-Z']/g, '').toLowerCase();
    const alterna = (w) => {
        if (enviado) return;
        setElegidas(p => p.includes(w) ? p.filter(x => x !== w) : [...p, w]);
    };

    const comprueba = () => {
        setEnviado(true);
        const objetivo = (ex.targetWords || []).map(w => w.toLowerCase());
        onResuelto?.(objetivo.every(t => elegidas.some(p => limpia(p) === t)));
    };

    return (
        <div className="ew-root">
            <p className="ew-hint">Pick the key words from this part:</p>
            <div className="ew-wordselect">
                {palabras.map((palabra, i) => {
                    const esObjetivo = (ex.targetWords || []).map(w => w.toLowerCase()).includes(limpia(palabra));
                    const elegida = elegidas.includes(palabra);
                    let cls = 'ew-word';
                    if (elegida) cls += ' picked';
                    if (enviado && esObjetivo) cls += ' target';
                    if (enviado && elegida && !esObjetivo) cls += ' wrong-pick';
                    return <span key={i} className={cls} onClick={() => alterna(palabra)}>{palabra}</span>;
                })}
            </div>
            {!enviado && <button className="ew-check-btn" onClick={comprueba}>Check</button>}
            {enviado && (
                <>
                    <p className="ew-respuestas">Palabras clave: {(ex.targetWords || []).join(', ')}</p>
                    <Explicacion texto={ex.explanation} />
                </>
            )}
        </div>
    );
}

/* ── PAREJAS ─────────────────────────────────────────────────────────────── */
//  Rob, 06-10-2026: «al seleccionar no se ve como si la marcara para unir ni
//  efecto al pasar el mouse de donde conectarla, no se sabe qué se está
//  seleccionando ni con quién».
//
//  Lo que había: la ficha elegida se pintaba del color de marca —el mismo que
//  usa media app— y la otra columna no cambiaba en nada. Un fallo al unir sólo
//  limpiaba la selección 400 ms después, sin decir que había fallado, así que
//  parecía que el clic no había hecho nada.
//
//  Y además NO SE PODÍA SUSPENDER: `onCorrect` se llamaba al completar las
//  parejas y `onWrong` no se llamaba nunca. Daba igual cuántas veces fallaras:
//  el marcador de arriba lo contaba como acierto. Ésa es la mitad de «✅ 4/5
//  correctas está desconectado».
function Parejas({ ex, onResuelto }) {
    const parejas = ex.pairs || [];
    const [izq, setIzq] = useState(null);         // índice elegido a la izquierda
    const [der, setDer] = useState(null);         // texto elegido a la derecha
    const [unidas, setUnidas] = useState({});     // { indiceIzq: textoDer }
    const [fallo, setFallo] = useState(null);     // { izq, der } mientras parpadea
    const [fallos, setFallos] = useState(0);
    const [cerrado, setCerrado] = useState(false);
    //  Se baraja UNA vez. Con `useState(() => …)` el orden no cambia en cada
    //  render: barajar en el cuerpo haría bailar las definiciones al pulsar.
    const [derechas] = useState(() => [...parejas.map(p => p.right)].sort(() => Math.random() - 0.5));

    const yaUnidaDer = (texto) => Object.values(unidas).includes(texto);
    const bloqueado = cerrado || fallo !== null;

    const intenta = (indiceIzq, textoDer) => {
        if (parejas[indiceIzq]?.right === textoDer) {
            const nuevas = { ...unidas, [indiceIzq]: textoDer };
            setUnidas(nuevas);
            setIzq(null); setDer(null);
            if (Object.keys(nuevas).length === parejas.length) {
                setCerrado(true);
                //  Acierto sólo si se completó SIN fallar. Es lo que mide el
                //  ejercicio: unir a base de probar no es saber la respuesta.
                onResuelto?.(fallos === 0);
            }
            return;
        }
        //  Falla: se marca en rojo en los dos lados a la vez y se suelta sola.
        //  Antes se limpiaba en silencio y parecía que el clic no había ido.
        setFallos(f => f + 1);
        setFallo({ izq: indiceIzq, der: textoDer });
        setTimeout(() => { setFallo(null); setIzq(null); setDer(null); }, 650);
    };

    const pulsaIzq = (i) => {
        if (bloqueado || unidas[i] !== undefined) return;
        if (izq === i) { setIzq(null); return; }   // volver a pulsarla la suelta
        setIzq(i);
        if (der !== null) intenta(i, der);
    };

    const pulsaDer = (texto) => {
        if (bloqueado || yaUnidaDer(texto)) return;
        if (der === texto) { setDer(null); return; }
        setDer(texto);
        if (izq !== null) intenta(izq, texto);
    };

    //  El número de la pareja, en los dos lados. Es lo que contesta «¿con quién
    //  se unió?» sin tener que acordarse de lo que se pulsó hace tres clics.
    const numeroDe = (indiceIzq) => Object.keys(unidas).indexOf(String(indiceIzq)) + 1;

    const claseFicha = (estado) => ['ew-par-ficha', ...estado].filter(Boolean).join(' ');
    const hayElegido = izq !== null || der !== null;

    return (
        <div className="ew-root">
            <div className="ew-par-cabecera">
                <p className="ew-hint">
                    {hayElegido
                        ? (izq !== null ? 'Now pick its meaning →' : '← Now pick the word')
                        : 'Match each word with its meaning'}
                </p>
                <span className="ew-par-cuenta">
                    {Object.keys(unidas).length} de {parejas.length}
                </span>
            </div>

            <div className="ew-par-columnas">
                <div className="ew-par-columna">
                    {parejas.map((pareja, i) => {
                        const unida = unidas[i] !== undefined;
                        const elegida = izq === i;
                        const fallando = fallo?.izq === i;
                        //  Cuando hay algo elegido en la OTRA columna, lo que
                        //  queda libre aquí se marca como posible destino: ésa
                        //  es la pregunta que se está haciendo el alumno.
                        const candidata = !unida && !elegida && der !== null;
                        return (
                            <button
                                key={`i-${i}`}
                                className={claseFicha([unida && 'unida', elegida && 'elegida',
                                                       fallando && 'fallando', candidata && 'candidata'])}
                                onClick={() => pulsaIzq(i)}
                                disabled={unida || cerrado}
                            >
                                {unida && <span className="ew-par-numero">{numeroDe(i)}</span>}
                                <span className="ew-par-texto">{pareja.left}</span>
                            </button>
                        );
                    })}
                </div>

                <div className="ew-par-columna">
                    {derechas.map((texto, i) => {
                        const unida = yaUnidaDer(texto);
                        const elegida = der === texto;
                        const fallando = fallo?.der === texto;
                        const candidata = !unida && !elegida && izq !== null;
                        const suIndice = Object.keys(unidas).find(k => unidas[k] === texto);
                        return (
                            <button
                                key={`d-${i}`}
                                className={claseFicha([unida && 'unida', elegida && 'elegida',
                                                       fallando && 'fallando', candidata && 'candidata'])}
                                onClick={() => pulsaDer(texto)}
                                disabled={unida || cerrado}
                            >
                                {unida && <span className="ew-par-numero">{numeroDe(Number(suIndice))}</span>}
                                <span className="ew-par-texto">{texto}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {cerrado && (
                <>
                    <p className="ew-respuestas">
                        {fallos === 0
                            ? 'Las uniste todas a la primera.'
                            : `Completado con ${fallos} ${fallos === 1 ? 'intento fallido' : 'intentos fallidos'}.`}
                    </p>
                    <Explicacion texto={ex.explanation} />
                </>
            )}
        </div>
    );
}

/* ── ARRASTRAR ───────────────────────────────────────────────────────────── */
function Arrastrar({ ex, onResuelto }) {
    const trozos = ex.template?.split('____') || [];
    const [huecos, setHuecos] = useState(Array(Math.max(0, trozos.length - 1)).fill(null));
    const [disponibles, setDisponibles] = useState(
        () => ex.correctWords ? [...ex.correctWords, ...(ex.distractors || [])].sort(() => Math.random() - 0.5) : []
    );
    const [enviado, setEnviado] = useState(false);
    const [elegida, setElegida] = useState(null);

    const pulsaHueco = (i) => {
        if (enviado) return;
        if (elegida) {
            const nuevos = [...huecos];
            const libres = disponibles.filter(w => w !== elegida);
            if (nuevos[i]) libres.push(nuevos[i]);
            nuevos[i] = elegida;
            setHuecos(nuevos);
            setDisponibles(libres);
            setElegida(null);
        } else if (huecos[i]) {
            setDisponibles([...disponibles, huecos[i]]);
            const nuevos = [...huecos];
            nuevos[i] = null;
            setHuecos(nuevos);
        }
    };

    const comprueba = () => {
        setEnviado(true);
        onResuelto?.((ex.correctWords || []).every((cw, i) => huecos[i] === cw));
    };

    return (
        <div className="ew-root">
            <p className="ew-hint">Pick a word, then click a blank to place it:</p>
            <div className="ew-fill-template" style={{ lineHeight: 2.2 }}>
                {trozos.map((trozo, i) => (
                    <React.Fragment key={i}>
                        <span>{trozo}</span>
                        {i < trozos.length - 1 && (
                            <button
                                className={`ew-dd-hueco ${huecos[i] ? 'lleno' : ''} ${enviado ? (huecos[i] === ex.correctWords?.[i] ? 'correct' : 'wrong') : ''}`}
                                onClick={() => pulsaHueco(i)}
                                disabled={enviado}
                            >
                                {huecos[i] || '____'}
                            </button>
                        )}
                    </React.Fragment>
                ))}
            </div>

            <div className="ew-dd-banco">
                {disponibles.map((palabra, i) => (
                    <button
                        key={i}
                        className={`ew-dd-palabra ${elegida === palabra ? 'elegida' : ''}`}
                        onClick={() => !enviado && setElegida(elegida === palabra ? null : palabra)}
                        disabled={enviado}
                    >
                        {palabra}
                    </button>
                ))}
                {disponibles.length === 0 && <span className="ew-dd-vacio">No quedan palabras…</span>}
            </div>

            {!enviado && huecos.every(v => v !== null) && (
                <button className="ew-check-btn" onClick={comprueba}>Check</button>
            )}
            {enviado && (
                <>
                    <p className="ew-respuestas">Answers: {ex.correctWords?.join(', ')}</p>
                    <Explicacion texto={ex.explanation} />
                </>
            )}
        </div>
    );
}

/* ── LA EXPLICACIÓN ──────────────────────────────────────────────────────── */
//  Se lee sin prisa: desde el 06-10-2026 el vídeo no sigue solo, espera a que
//  el alumno pulse «Continuar». Si la explicación está vacía se dice, en vez de
//  dejar un hueco que parece un fallo de carga.
function Explicacion({ texto }) {
    const hay = typeof texto === 'string' && texto.trim();
    return (
        <p className={`ew-explanation ${hay ? '' : 'ew-explanation--vacia'}`}>
            {hay ? texto : 'No explanation for this one.'}
        </p>
    );
}
